import { useContext, useState } from 'react';
import { slide as Menu } from 'react-burger-menu';
import { useNavigate, useLocation } from 'react-router-dom';
import { GrLogin, GrLogout } from 'react-icons/gr';
import { VscPieChart } from 'react-icons/vsc';

import MobileAccordionSection from './mobile-accordion-section/mobile-accordion-section.component.jsx';
import Spinner from '../../reusable/spinner/spinner.component';

import { ConfigurationContext } from '../../../contexts/configuration.context.jsx';
import { UserContext } from '../../../contexts/user.context';

import { groupedMenuItemsAdmin, groupedMenuItemsContributor } from '../../../assets/menu-items';
import { api, tokenName } from '../../../config.js';

import './hamburger-menu.css';
import {
  HeaderLink,
  LogoutDivider,
  MobileDropDownMenu,
  MobileDropDownMenuItem,
  MobileDropDownMenuLastItem,
  MobileDropDownMenuLink,
  Logo,
  LogoContainer,
  LogoLink,
} from './hamburger-menu.styles';

const isActiveRoute = (pathname, itemPath) => {
  if (itemPath === '/') return pathname === '/';
  return pathname === itemPath || pathname.startsWith(itemPath + '/');
};

const findActiveGroupTitle = (groups, pathname) => {
  const group = groups.find(g =>
    g.items.some(item => isActiveRoute(pathname, item.path))
  );
  return group ? group.title : null;
};

const HamburgerMenu = () => {
  const { configuration, rectangleLogo } = useContext(ConfigurationContext);
  const { currentUser } = useContext(UserContext);
  const loggedInStatus = localStorage.getItem(tokenName);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [openSection, setOpenSection] = useState(null);

  const handleNavigate = (path) => {
    setMenuOpen(false);
    navigate(path);
  };

  const handleLogout = () => {
    localStorage.removeItem(tokenName);
    window.location = '/';
  };

  const handleToggleSection = (title) => {
    setOpenSection(prev => (prev === title ? null : title));
  };

  const renderMenuGroups = (groups) => {
    // Skip the Dashboard group — it's rendered as a standalone link
    const accordionGroups = groups.filter(g => g.title !== 'Dashboard');

    // Auto-open the section containing the active route on first render
    const activeTitle = openSection ?? findActiveGroupTitle(accordionGroups, pathname);

    return accordionGroups.map((group) => (
      <MobileAccordionSection
        key={group.title}
        title={group.title}
        isOpen={activeTitle === group.title}
        onToggle={() => handleToggleSection(group.title)}
      >
        {group.items.map((item, subIdx) => {
          const active = isActiveRoute(pathname, item.path);
          return (
            <MobileDropDownMenuItem
              key={subIdx}
              active={active}
              onClick={() => handleNavigate(item.path)}
            >
              {item.icon}
              <MobileDropDownMenuLink as="span">
                {item.title}
              </MobileDropDownMenuLink>
            </MobileDropDownMenuItem>
          );
        })}
      </MobileAccordionSection>
    ));
  };

  return (
    <Menu isOpen={menuOpen} onStateChange={({ isOpen }) => setMenuOpen(isOpen)}>
      {!configuration ? (
        <Spinner />
      ) : (
        <>
          <MobileDropDownMenu>
            <LogoContainer>
              {rectangleLogo && (
                <LogoLink onClick={() => handleNavigate('/')}>
                  <Logo src={api + rectangleLogo.path + '.webp'} alt="Mobile Logo" />
                </LogoLink>
              )}
            </LogoContainer>

            {loggedInStatus ? (
              <>
                {currentUser === null ? (
                  <Spinner />
                ) : (
                  <>
                    {currentUser.roleId < 3 && (
                      <>
                        <MobileDropDownMenuItem
                          active={isActiveRoute(pathname, '/')}
                          onClick={() => handleNavigate('/')}
                        >
                          <VscPieChart />
                          <MobileDropDownMenuLink as="span">
                            Dashboard
                          </MobileDropDownMenuLink>
                        </MobileDropDownMenuItem>
                        {renderMenuGroups(groupedMenuItemsAdmin)}
                      </>
                    )}
                    {currentUser.roleId === 5 && renderMenuGroups(groupedMenuItemsContributor)}
                  </>
                )}

                <LogoutDivider />
                <MobileDropDownMenuLastItem onClick={handleLogout}>
                  <GrLogout color="white" />
                  <HeaderLink as="span">Log Out</HeaderLink>
                </MobileDropDownMenuLastItem>
              </>
            ) : (
              <MobileDropDownMenuLastItem>
                <GrLogin color="white" />
                <HeaderLink href={`/login`}>Log In</HeaderLink>
              </MobileDropDownMenuLastItem>
            )}
          </MobileDropDownMenu>
        </>
      )}
    </Menu>
  );
};

export default HamburgerMenu;
